import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { FluentProvider, Spinner, webLightTheme } from '@fluentui/react-components';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { CoherencePanelStyles } from '../../app/common/styles/CommonStyleObjects';
import hasAccess from '../../utils/authorization/authorizationCheck';
import UserContext from '../../utils/authorization/UserContext';
import momentUtc from '../../utils/DateFormatter';
import { GetStatUserDetailQuery, StatUser } from '../../utils/statApi/UsersApi';
import StatDateFormats from '../../utils/types/DateFormats';
import { IServiceContactWithCompany } from '../companies/serviceContacts/interfaces';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import SpanWithLabel from '../common/SpanWithLabel';
import SecondaryFormFields from '../users/SecondaryFormFields';

interface IInvalidServiceContactPanelProps {
  contact: IServiceContactWithCompany;
  onDismiss: () => void;
}

export const canViewContactRoles = (user: StatUser): boolean =>
  // This panel is restricted to global Admins, not all users with user-list access.
  !!user?.userRoles?.some((role) => role.groupIdentifier === 'GLOBAL' && role.roleId === 1) &&
  hasAccess(user?.permissions, 'UsersList:View');

const ContactDetails = ({ contact }: { contact: IServiceContactWithCompany }): JSX.Element => {
  const { data, loading, error } = useQuery<{ statUserDetail: StatUser }>(GetStatUserDetailQuery, {
    variables: { graphGUID: contact.graphId },
    fetchPolicy: 'no-cache',
  });
  const statUser = data?.statUserDetail;

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      <SpanWithLabel labelText="Name" value={contact.name} />
      <SpanWithLabel labelText="User Alias" value={contact.alias} />
      <SpanWithLabel labelText="Email" value={contact.email} />
      {loading && <Spinner label="Loading contact role assignments" />}
      <LoadingErrorMessage
        loading={loading}
        error={error}
        actionName="loading contact role assignments"
      />
      {!loading && !error && !statUser && <p>No STAT user record was found for this contact.</p>}
      {!loading && !error && statUser && (
        <>
          <SpanWithLabel labelText="Status" value={statUser.isActive ? 'Active' : 'Deactivated'} />
          <SpanWithLabel
            labelText="Last Login"
            value={momentUtc(statUser.lastLoginDate, StatDateFormats.DaySimple, true)}
          />
          <SecondaryFormFields statUser={statUser} />
        </>
      )}
    </Stack>
  );
};

const InvalidServiceContactPanel = ({
  contact,
  onDismiss,
}: IInvalidServiceContactPanelProps): JSX.Element => {
  const user: StatUser = useContext(UserContext);
  if (!canViewContactRoles(user)) {
    return null;
  }

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Service Contact Details"
      isOpen
      onDismiss={onDismiss}
      styles={CoherencePanelStyles}
      hasCloseButton
      closeButtonAriaLabel="Close Service Contact Details"
    >
      <FluentProvider theme={webLightTheme}>
        <ContactDetails contact={contact} />
      </FluentProvider>
    </CoherencePanel>
  );
};

export default InvalidServiceContactPanel;
